import { FC, useMemo, useState } from "react";
import { StyledButton } from "@/components/styled-button";
import { Divider } from "@/components/divider";

import { ReactComponent as CopyIcon } from "@/assets/icons/copy.svg";
import { ReactComponent as CheckIcon } from "@/assets/icons/check.svg";
import { ReactComponent as CloseIcon } from "@/assets/icons/close.svg";
import { useAppSelector } from "@/store/app/hooks";
import { loadDataSelector } from "@/store/features/load-info/loadInfoSelectors";
import { sendAmplitudeData } from "@/analytics";
import { AMPLITUDE_EVENTS } from "@/analytics/events";
import { ILoadDataV2 } from "@/types/common";
import { emailDataSelector } from "@/store/features/email/emailSelectors";
import { carrierReportDataSelector } from "@/store/features/carrier-report/carrierReportSelectors";
import { isBrokerSelector } from "@/store/features/tabs/tabsSelectors";

const formatLoadInfoForCopy = (
  data: ILoadDataV2,
  email?: string,
  phone?: string
) => {
  const formattedData = `
MC: ${data.mc || "N/A"}
Company: ${data.company || "N/A"}
Available: ${data.pickup_date || "N/A"}
Load:
  Origin: ${data.origin || "N/A"}
  Destination: ${data.destination || "N/A"}
  Miles: ${data.trip || "N/A"}
Rate:
  Total: ${data.rate || "N/A"}
  Per Mile: ${data.rpm || "N/A"}
LoadInfo:
  Reference Id: ${data.refId || "N/A"}
  Load: ${data.load_type || "N/A"}
  Truck: ${data.equipment.truck || "N/A"}
  Length: ${data.equipment.length || "N/A"} 
  Weight: ${data.equipment.weight || "N/A"}
Contact:
  Email: ${email || "N/A"}
  Phone: ${data.phone || "N/A"}
  `;

  return formattedData;
};

export const Header: FC = () => {
  const [isCopied, setIsCopied] = useState(false);
  const loadData = useAppSelector(loadDataSelector);
  const emailData = useAppSelector(emailDataSelector);
  const carrierReportData = useAppSelector(carrierReportDataSelector);
  const isBroker = useAppSelector(isBrokerSelector);
  const factoringData = carrierReportData?.factoringInfo?.[0];

  const { origin, destination, contact, phone } = loadData || {};
  const { email } = emailData || {};

  const renderEmail = useMemo(() => {
    if (!isBroker) {
      return email || (contact && contact?.includes("@") ? contact : "");
    }
    return carrierReportData?.emailAddress || factoringData?.email;
  }, [
    carrierReportData?.emailAddress,
    factoringData?.email,
    contact,
    email,
    isBroker,
  ]);

  const renderPhone = useMemo(() => {
    if (!isBroker) {
      return phone ? (typeof phone === "string" ? phone : phone?.phone) : "";
    }
    return carrierReportData?.phone || factoringData?.phone;
  }, [factoringData?.phone, carrierReportData?.phone, isBroker, phone]);

  const closeModal = () => {
    window.parent.postMessage("closeldcmodal", "*");
    sendAmplitudeData(AMPLITUDE_EVENTS.popupClosed);
  };

  const handleCopyLoadInfo = async () => {
    if (!loadData) return;
    const formattedData = formatLoadInfoForCopy(
      loadData,
      renderEmail,
      renderPhone
    );

    try {
      await navigator.clipboard.writeText(formattedData);
    } catch (error) {
      const textArea = document.createElement("textarea");
      textArea.value = formattedData;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
      } catch (error) {
        console.error("Failed to copy", error);
      }
      document.body.removeChild(textArea);
    }

    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <div className="flex justify-between items-center p-4 text-xl font-semibold text-text-secondary overflow-hidden">
      <h1>
        <span className="text-text-primary font-bold">{origin}</span>
        {destination && " -> "}
        <span className="text-text-primary font-bold">{destination}</span>
      </h1>
      <div className="flex gap-3 items-center h-7">
        <StyledButton
          color="primary"
          variant="light"
          startContent={
            isCopied ? (
              <CheckIcon width={16} height={16} />
            ) : (
              <CopyIcon width={16} height={16} />
            )
          }
          onPress={handleCopyLoadInfo}
        >
          {isCopied ? "Copied" : "Copy Load Info"}
        </StyledButton>
        <Divider />
        <StyledButton isIconOnly size="sm" variant="light" onPress={closeModal}>
          <CloseIcon />
        </StyledButton>
      </div>
    </div>
  );
};
